export const STORE_SEARCH_FIELDS = ["all", "name", "id", "market", "address"] as const

export type StoreSearchField = (typeof STORE_SEARCH_FIELDS)[number]

const STORE_SEARCH_LABELS: Record<StoreSearchField, string> = {
  all: "All",
  name: "Store",
  id: "Store id",
  market: "Market",
  address: "Address",
}

export type StoreSearchOption = { value: StoreSearchField; label: string }

const optionsFor = (fields: readonly StoreSearchField[]): StoreSearchOption[] =>
  fields.map((value) => ({ value, label: STORE_SEARCH_LABELS[value] }))

/** Every field a store row can be searched by. */
export const STORE_SEARCH_OPTIONS = optionsFor(STORE_SEARCH_FIELDS)
/** For tables whose rows carry no address. */
export const STORE_SEARCH_OPTIONS_NO_ADDRESS = optionsFor(["all", "name", "id", "market"])
/** For tables that list a store with its city only. */
export const STORE_SEARCH_OPTIONS_NAME_AND_ADDRESS = optionsFor(["all", "name", "address"])

export function toStoreSearchField(
  value: string,
  options: readonly StoreSearchOption[] = STORE_SEARCH_OPTIONS,
): StoreSearchField {
  return options.find((option) => option.value === value)?.value ?? "all"
}

/** A store row's searchable fields; every table maps its own row onto this. */
export type StoreSearchRow = {
  name: string
  /** The store's ids: the source system id, the code, the location id. */
  ids?: readonly (string | null | undefined)[]
  market?: string | null
  address?: string | null
}

type SearchText = string | null | undefined

function searchedTexts(row: StoreSearchRow, searchBy: StoreSearchField): readonly SearchText[] {
  switch (searchBy) {
    case "name":
      return [row.name]
    case "id":
      return row.ids ?? []
    case "market":
      return [row.market]
    case "address":
      return [row.address]
    case "all":
      return [row.name, ...(row.ids ?? []), row.market, row.address]
  }
}

/** Case-insensitive substring search over the fields the option names. */
export function matchesStoreSearch(
  row: StoreSearchRow,
  search: string,
  searchBy: StoreSearchField,
): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === "") return true
  return searchedTexts(row, searchBy).some((text) => text?.toLowerCase().includes(needle) ?? false)
}
